import React from 'react'
import { useAccountListUsersMutation } from '@mote/api'
import { useQueryParams } from '@mote/helpers'
import { Alert, Block, BlockGroup, Spinner, Tooltip } from '@mote/ui'
import styled from 'styled-components'
import { EpochToPrettyDate, getGodModeOptions } from '../../shared/Utils'
import { UserControls } from './users/UserControls'
import { AccountPaginator } from './AccountPaginator'

const godModeOptions = getGodModeOptions()

export const AccountUsersManagement = ({ account, onAccountUpdated }) => {
  const { queryParams, setQueryParams } = useQueryParams()
  const {
    mutate,
    data: users,
    loading,
    error,
    fetched
  } = useAccountListUsersMutation()

  React.useEffect(() => {
    if (!queryParams.page && queryParams.page !== 0) {
      setQueryParams({ page: 0 })
      return
    }

    if (Number.isNaN(queryParams.page)) {
      setQueryParams({ page: 0 })
      return
    }

    mutate({ variables: { pageIndex: parseInt(queryParams.page) } })
  }, [queryParams])

  React.useEffect(() => {
    mutate({ variables: { pageIndex: parseInt(queryParams.page) } })
  }, [account])

  if (error) {
    return (
      <Alert
        type='error'
        message='An error occurred while loading users. Please refresh the page and if the problem persists contact support at support@mote.com'
      />
    )
  }

  if (loading || !fetched) {
    return (
      <BlockGroup>
        <Spinner radius={35} />
      </BlockGroup>
    )
  }

  return (
    <BlockGroup>
      {users.userCount === 0 && (
        <Alert
          type='warn'
          message=" We cannot find any users assigned. This shouldn't happen. Contact
            support@mote.com if this persists."
        />
      )}

      {users.userCount > 0 && (
        <Block>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th scope='col'>Email</th>
                  <th scope='col'>Name</th>
                  <th scope='col'>Under 18?</th>
                  <th scope='col'>Plan</th>
                  <th scope='col'>Role</th>
                  <th scope='col'>Last sign in</th>
                  <th scope='col'>Motes recorded</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {users.currentPage.map(r => {
                  return (
                    <tr key={r.primaryEmailAddress}>
                      <td>
                        <Tooltip content={r.primaryEmailAddress}>
                          <TruncatedText>{r.primaryEmailAddress}</TruncatedText>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip content={r.name}>{r.name}</Tooltip>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {r.minorhood === 'Y' ? 'Yes' : '﹣'}
                      </td>
                      <td>{r.productName}</td>
                      <td>{r.institutionalRole}</td>
                      <td>{r.name && EpochToPrettyDate(r.lastSignInEpoch)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {r.totalMotesRecorded}
                      </td>
                      <td>
                        <UserControls
                          userRow={r}
                          godModeOptions={godModeOptions}
                          pageIndex={parseInt(queryParams.page)}
                          afterUpdateCallback={o => {
                            onAccountUpdated()

                            mutate({
                              variables: {
                                pageIndex: parseInt(queryParams.page)
                              }
                            })
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableContainer>
          <AccountPaginator
            pageIndex={queryParams.page}
            pageIndexes={users.pageIndexes}
          />
        </Block>
      )}
    </BlockGroup>
  )
}

const TableContainer = styled(Block)`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    padding: 22px 16px;
    font-weight: 400;
    font-size: 15px;
    line-height: 100%;
    border-top: 1px solid #d1d1e0;
    background-color: #f9f9fb;
  }

  td {
    padding: 12px 16px;
    border-top: 1px solid #d1d1e0;
    text-align: left;
    font-size: 15px;
    line-height: 100%;
    color: #646f7b;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;
    white-space: nowrap;
  }

  thead th {
    border: 0;
    font-weight: 500;
    white-space: nowrap;
    vertical-align: bottom;
  }

  tbody tr {
    &:hover {
      background: rgba(213, 212, 218, 0.25);
    }
  }
`

const TruncatedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`
